import React, { useCallback, useMemo } from 'react'
import { token } from '@atlaskit/tokens'
import Spinner from '@atlaskit/spinner'

// Status icons
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle'
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle'
import WarningIcon from '@atlaskit/icon/glyph/warning'
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle'
import EditorHorizontalRuleIcon from '@atlaskit/icon/glyph/editor/horizontal-rule'
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done'
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove'

/**
 * STATUS SELECTOR COMPONENT
 *
 * Grid-based status selector for execution details modal.
 * Displays all available project statuses with visual feedback.
 *
 * Features:
 * - Grid layout (3 columns)
 * - Current status highlighted
 * - Status icons based on status name
 * - Loading state during update
 * - Hover effects
 *
 * @param {Array} statuses - Array of status objects from projectStatusesSlice
 * @param {number} currentStatusId - Currently selected status ID
 * @param {function} onStatusChange - Callback when status is selected (receives status object)
 * @param {boolean} isLoading - Show loading state during update
 */
export function StatusSelector({ statuses, currentStatusId, onStatusChange, isLoading = false }) {
  /**
   * GET STATUS SORT PRIORITY
   * Returns priority number for sorting statuses in correct order.
   */
  const getStatusPriority = useCallback((statusName) => {
    const name = statusName?.toLowerCase()
    if (name === 'pass' || name === 'passed') return 1
    if (name === 'fail' || name === 'failed') return 2
    if (name === 'block' || name === 'blocked') return 3
    if (name === 'query') return 4
    if (name === 'exclude' || name === 'excluded') return 5
    return 6 // Other custom statuses
  }, [])

  /**
   * SORTED STATUSES
   * Statuses sorted in order: Pass, Fail, Blocked, Query, Exclude, others
   */
  const sortedStatuses = useMemo(() => {
    return [...statuses].sort((a, b) => {
      return getStatusPriority(a.name) - getStatusPriority(b.name)
    })
  }, [statuses, getStatusPriority])

  /**
   * MAP STATUS NAME TO ICON COMPONENT
   */
  const getStatusIcon = useCallback((statusName) => {
    const name = statusName?.toLowerCase()

    if (name === 'pass' || name === 'passed') return CheckCircleIcon
    if (name === 'fail' || name === 'failed') return CrossCircleIcon
    if (name === 'block' || name === 'blocked') return WarningIcon
    if (name === 'query') return QuestionCircleIcon
    if (name === 'exclude' || name === 'excluded') return EditorHorizontalRuleIcon

    // Default icon for custom statuses
    return EditorDoneIcon
  }, [])

  /**
   * GET ICON COLOR FOR STATUS
   * Returns semantic token color for status icons in the grid
   */
  const getStatusIconColor = useCallback((statusName, statusColor) => {
    const name = statusName?.toLowerCase()
    if (name === 'pass' || name === 'passed') return token('color.icon.success')
    if (name === 'fail' || name === 'failed') return token('color.icon.danger')
    if (name === 'block' || name === 'blocked') return token('color.icon.warning')
    if (name === 'query') return token('color.icon.information')
    if (name === 'exclude' || name === 'excluded') return token('color.icon.subtle')

    // Use the status color if available
    if (statusColor) return statusColor

    return token('color.icon.subtle')
  }, [])

  /**
   * GET BACKGROUND COLOR FOR STATUS BADGE
   * Returns appropriate background color for the current status display pill.
   * Uses color.background.*.bold tokens which are designed for solid colored backgrounds
   * with white text. These tokens maintain consistent, vibrant colors in both light and dark themes.
   * Note: color.icon.* tokens should NOT be used for backgrounds as they become lighter in dark mode.
   */
  const getStatusBackgroundColor = useCallback((statusName, statusColor) => {
    const name = statusName?.toLowerCase()
    if (name === 'pass' || name === 'passed') return token('color.background.success.bold', '#1F845A')
    if (name === 'fail' || name === 'failed') return token('color.background.danger.bold', '#CA3521')
    if (name === 'block' || name === 'blocked') return token('color.icon.warning', '#E06C00')
    if (name === 'query') return token('color.background.information.bold', '#0055CC')
    if (name === 'exclude' || name === 'excluded') return token('color.background.neutral.bold', '#626F86')

    // Use the status color if available, fallback to neutral
    if (statusColor) return statusColor
    return token('color.background.neutral.bold', '#626F86')
  }, [])

  /**
   * HANDLE STATUS CLICK
   */
  const handleStatusClick = useCallback((status) => {
    // Don't allow clicking current status or when loading
    if (status.id === currentStatusId || isLoading) return

    onStatusChange(status)
  }, [currentStatusId, isLoading, onStatusChange])

  return (
    <div style={{ position: 'relative' }}>
      {/* Current Status Display - Always shown, displays "Cleared" when no status */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: token('color.text.subtle', '#5E6C84'),
            marginBottom: '8px'
          }}
        >
          Current Status
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {currentStatusId ? (
            // Show current status badge when status is set
            statuses.map(status => {
              if (status.id === currentStatusId) {
                return (
                  <div
                    key={status.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      backgroundColor: getStatusBackgroundColor(status.name, status.color),
                      color: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: 600,
                      gap: '6px'
                    }}
                  >
                    {React.createElement(getStatusIcon(status.name), {
                      size: 'small',
                      label: status.name,
                      primaryColor: '#FFFFFF'
                    })}
                    {status.name}
                  </div>
                )
              }
              return null
            })
          ) : (
            // Show "Cleared" badge when no status is set
            // Uses neutral background token that adapts to theme, matches size/style of other status badges
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 12px',
                borderRadius: '16px',
                backgroundColor: token('color.background.neutral', '#DFE1E6'),
                color: token('color.text', '#172B4D'),
                fontSize: '13px',
                fontWeight: 600,
                gap: '6px'
              }}
            >
              Cleared
            </div>
          )}
          {/* Inline spinner when updating */}
          {isLoading && <Spinner size="small" />}
        </div>
      </div>

      {/* Status Selection Grid */}
      <div style={{ marginBottom: '8px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: token('color.text.subtle', '#5E6C84'),
            marginBottom: '12px'
          }}
        >
          {currentStatusId ? 'Change Status' : 'Select Status'}
        </label>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px'
          }}
        >
          {sortedStatuses.map(status => {
            const isSelected = status.id === currentStatusId
            const StatusIcon = getStatusIcon(status.name)
            const iconColor = getStatusIconColor(status.name, status.color)

            return (
              <button
                key={status.id}
                onClick={() => handleStatusClick(status)}
                disabled={isSelected || isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '8px',
                  // Compensate padding for border thickness change (2px selected vs 1px unselected)
                  padding: isSelected ? '10px 12px' : '11px 13px',
                  borderRadius: '4px',
                  border: isSelected
                    ? `2px solid ${status.color || token('color.border.selected', '#0052CC')}`
                    : `1px solid ${token('color.border', '#DFE1E6')}`,
                  backgroundColor: isSelected
                    ? token('color.background.selected', '#DEEBFF')
                    : token('color.background.neutral', '#F4F5F7'),
                  cursor: isSelected || isLoading ? 'default' : 'pointer',
                  opacity: isLoading && !isSelected ? 0.6 : 1,
                  transition: 'all 0.15s ease',
                  outline: 'none',
                  width: '100%',
                  // Fixed height to prevent layout shift when selection changes
                  height: '42px',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isLoading) {
                    e.currentTarget.style.backgroundColor = token('color.background.neutral.hovered', '#EBECF0')
                    e.currentTarget.style.borderColor = status.color || token('color.border.focused', '#4C9AFF')
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = token('color.background.neutral', '#F4F5F7')
                    e.currentTarget.style.borderColor = token('color.border', '#DFE1E6')
                  }
                }}
              >
                {/* Status icon */}
                <StatusIcon
                  size="small"
                  label={status.name}
                  primaryColor={iconColor}
                />

                {/* Status name */}
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected
                      ? token('color.text.selected', '#0052CC')
                      : token('color.text', '#172B4D'),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {status.name}
                </span>

                {/* Selected checkmark - always reserve space to prevent layout shift */}
                <span style={{
                  marginLeft: 'auto',
                  visibility: isSelected ? 'visible' : 'hidden'
                }}>
                  <CheckCircleIcon
                    size="small"
                    label="Selected"
                    primaryColor={token('color.icon.success', '#1F845A')}
                  />
                </span>
              </button>
            )
          })}

          {/* Clear Status Button - 6th button in grid */}
          <button
            onClick={() => onStatusChange(null)}
            disabled={!currentStatusId || isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              // Same padding as unselected status buttons (compensate for 1px border)
              padding: '11px 13px',
              borderRadius: '4px',
              border: `1px solid ${token('color.border', '#DFE1E6')}`,
              backgroundColor: token('color.background.neutral', '#F4F5F7'),
              cursor: !currentStatusId || isLoading ? 'default' : 'pointer',
              opacity: !currentStatusId || isLoading ? 0.5 : 1,
              transition: 'all 0.15s ease',
              outline: 'none',
              width: '100%',
              // Fixed height to match other buttons
              height: '42px',
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              if (currentStatusId && !isLoading) {
                e.currentTarget.style.backgroundColor = token('color.background.neutral.hovered', '#EBECF0')
                e.currentTarget.style.borderColor = token('color.border.focused', '#4C9AFF')
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = token('color.background.neutral', '#F4F5F7')
              e.currentTarget.style.borderColor = token('color.border', '#DFE1E6')
            }}
          >
            {/* Clear icon */}
            <EditorRemoveIcon
              size="small"
              label="Clear"
              primaryColor={token('color.icon.subtle', '#6B778C')}
            />

            {/* Clear label */}
            <span
              style={{
                fontSize: '13px',
                fontWeight: 400,
                color: token('color.text', '#172B4D'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Clear
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default StatusSelector
